import React from 'react';
import {
  Checkbox,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
} from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import {
  EnhancedTableColumn,
  KeyboardNavigationConfig,
  UseInlineEditReturn,
  UseKeyboardNavigationReturn,
} from '../../types';
import { InlineEditableCell } from '../cells/inline-editable-cell';

interface EnhancedTableRowProps<T> {
  row: T;
  columns: EnhancedTableColumn<T>[];
  isSelected: boolean;
  isExpanded: boolean;
  onSelectRow: (id: string | number) => void;
  onExpandRow: (id: string | number) => void;
  renderExpandedContent?: (row: T) => React.ReactNode;
  getRowId: (row: T) => string | number;
  collapsible: boolean;
  isFullscreen: boolean;
  showSelection?: boolean;
  onContextMenu?: (
    event: React.MouseEvent,
    row: T,
    rowId: string | number,
  ) => void;
  // Inline editing props
  inlineEditHook?: UseInlineEditReturn<T>;
  inlineEditTriggerMode?: 'click' | 'doubleClick' | 'manual';
  // Keyboard navigation props
  keyboardNavigation?: UseKeyboardNavigationReturn & {
    config?: KeyboardNavigationConfig;
    clearSelection?: () => void;
  };
  rowIndex?: number;
  columnWidths?: Record<string, number>;
  tableLayout?: 'auto' | 'fixed';
}

export function EnhancedTableRow<T extends Record<string, unknown>>({
  row,
  columns,
  isSelected,
  isExpanded,
  onSelectRow,
  onExpandRow,
  renderExpandedContent,
  getRowId,
  collapsible,
  isFullscreen,
  showSelection = false,
  onContextMenu,
  inlineEditHook,
  inlineEditTriggerMode = 'doubleClick',
  keyboardNavigation,
  rowIndex = 0,
  columnWidths = {},
  tableLayout = 'auto',
}: EnhancedTableRowProps<T>) {
  const rowId = getRowId(row);

  return (
    <React.Fragment>
      <TableRow
        hover
        selected={isSelected}
        data-expanded={isExpanded}
        data-fullscreen={isFullscreen}
        onContextMenu={
          onContextMenu
            ? (event) => onContextMenu(event, row, rowId)
            : undefined
        }
      >
        {/* Always show selection checkbox if selection is enabled */}
        {showSelection && (
          <TableCell padding="checkbox">
            <Checkbox
              checked={isSelected}
              onChange={() => onSelectRow(rowId)}
            />
          </TableCell>
        )}

        {columns.map((column) => {
          if (column.id === 'select') {
            // Skip the select column since we handle it above
            return null;
          }

          if (column.id === '__expand__') {
            return (
              <TableCell key="__expand__">
                <IconButton size="small" onClick={() => onExpandRow(rowId)}>
                  <SystemIcon
                    iconName="CaretRight"
                    style={{
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition:
                        'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                </IconButton>
              </TableCell>
            );
          }

          const value = row[column.id];

          // Use InlineEditableCell for all editable columns (with or without inline editing)
          if (column.editable) {
            const columnIndex = columns.indexOf(column);
            const isFocused =
              keyboardNavigation?.focusedCell?.rowIndex === rowIndex &&
              keyboardNavigation?.focusedCell?.columnIndex === columnIndex;

            // Calculate width the same way as header cells
            // Only use explicit width if it's set in columnWidths or column.width
            // If neither is set, column will share remaining space
            const columnWidth =
              columnWidths[String(column.id)] ?? column.width ?? undefined;

            return (
              <InlineEditableCell
                key={String(column.id)}
                value={value}
                row={row}
                column={column}
                columnWidth={columnWidth}
                tableLayout={tableLayout}
                isEditing={inlineEditHook?.isEditing(rowId, column.id) || false}
                onStartEdit={() =>
                  inlineEditHook?.startEdit(rowId, column.id, value)
                }
                onCommit={(newValue) => {
                  inlineEditHook?.commitEdit(newValue);
                }}
                onCancel={() => inlineEditHook?.cancelEdit()}
                triggerMode={inlineEditTriggerMode}
                isFocused={isFocused}
                onCellFocus={() => {
                  keyboardNavigation?.setFocusedCell(rowIndex, columnIndex);
                  // Clear any row selection when focusing a cell via keyboard
                  keyboardNavigation?.clearSelection?.();
                }}
              />
            );
          }

          // Default non-editable cell
          const formattedValue = column.format
            ? column.format(value, row)
            : value;

          // Calculate width the same way as header cells
          // Fallback behavior depends on tableLayout:
          // - tableLayout: 'fixed': undefined allows columns to share remaining space
          // - tableLayout: 'auto' (default): 120px fallback for backward compatibility
          const columnWidth =
            columnWidths[String(column.id)] ??
            column.width ??
            (tableLayout === 'fixed' ? undefined : 120);

          return (
            <TableCell
              key={String(column.id)}
              align={column.align || 'left'}
              sx={{
                minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
                width: columnWidth ? `${columnWidth}px` : undefined,
                maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
                boxSizing: 'border-box',
              }}
            >
              {formattedValue as React.ReactNode}
            </TableCell>
          );
        })}
      </TableRow>

      {collapsible && isExpanded && renderExpandedContent && (
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={columns.length + (showSelection ? 1 : 0)}
          >
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {renderExpandedContent(row)}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}
